import {
    formatIsoDate,
    formatUnixDateTime,
} from "@core/domain/utils/date-format";
import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportDto } from "@report/data/dto/report.dto";
import { ReportDtoSchema } from "@report/data/dto/report.dto";
import { Report } from "@report/domain/models/report.model";
import { getUniqueSensorLabels } from "@station/domain/utils/sensor-label.utils";
import { t } from "i18next";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportMapper {
    map(reportDto: ReportDto): Result<Report[], ModelError<typeof Report>> {
        const reportDtoParsed = ReportDtoSchema.safeParse(reportDto);

        if (!reportDtoParsed.success) {
            console.error(reportDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const reportsResults = Object.values(reportDto.data).map((report) => {
            const station = report.station;
            const reportData = report.data;
            const reportPagination = report.pagination;
            const variableLabels = getUniqueSensorLabels(station.variables);

            const reportMapped = reportData.map((reportDataValues) => {
                const { timestamp, ...rest } = reportDataValues;

                const values = Object.entries(rest).reduce<
                    Record<string, string>
                >((acc, [key, value]) => {
                    const variable = station.variables.find(
                        (stationVar) => stationVar.id === Number(key),
                    );
                    const variableName = variable
                        ? (variableLabels.get(variable.id) ?? variable.name)
                        : key;
                    const symbol = variable?.symbol ?? "";

                    acc[variableName] = `${value} ${symbol}`;
                    return acc;
                }, {});

                const dateTranslated = t("common:date.date");
                return {
                    [dateTranslated]: formatUnixDateTime(timestamp),
                    ...values,
                };
            });

            const statsMapped = report.stats
                ? Object.entries(report.stats).map(([date, statByVariable]) => {
                      const row: Record<string, string | null> = {};
                      const dateTranslated = t("common:date.date");
                      row[dateTranslated] = formatIsoDate(date);

                      for (const [varId, stat] of Object.entries(
                          statByVariable,
                      )) {
                          const variable = station.variables.find(
                              (stationVar) => stationVar.id === Number(varId),
                          );
                          const varName = variable
                              ? (variableLabels.get(variable.id) ??
                                variable.name)
                              : varId;
                          const symbol = variable?.symbol ?? "";

                          row[`${varName} max`] = stat.max
                              ? `${stat.max.value} ${symbol}`
                              : null;
                          row[`${varName} min`] = stat.min
                              ? `${stat.min.value} ${symbol}`
                              : null;
                          row[`${varName} avg`] = stat.avg
                              ? `${stat.avg} ${symbol}`
                              : null;
                          if (stat.sum !== null) {
                              row[`${varName} sum`] = stat.sum
                                  ? `${stat.sum} ${symbol}`
                                  : null;
                          }
                      }

                      return row;
                  })
                : [];

            return Report.createWithRecovery({
                stationReportSummary: {
                    icon: station.icon,
                    stationId: station.id,
                    stationName: `${station.name} (${station.technical_name})`,
                },
                items: reportMapped,
                pagination: {
                    count: reportPagination.count,
                    current: reportPagination.current,
                    pages: reportPagination.pages,
                    perPage: reportPagination.per_page,
                },
                stats: statsMapped,
            });
        });

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
